import type { Release, UpdateReleaseRequest } from '@central/api';
import { Card, PageHeader, formatDate, isApiError } from '@central/ui';
import { Alert, Button, Group, Modal, Table, TextInput, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { keys, useApi } from '../api';
import { DataTable, Empty, QueryState } from '../components/parts';
import { errorMessage } from '../lib/errors';
import { fromDateInput, toDateInput } from '../lib/format';
import { mono } from '../lib/labels';
import { useCanWrite } from '../lib/me';

/** A stable release, MAJOR.MINOR.PATCH, or a hotfix of one (MAJOR.MINOR.PATCH.HOTFIX). */
const versionRe = /^\d+\.\d+\.\d+(\.\d+)?$/;

/** Longest highlights text, in characters (the server's limit). */
const maxHighlights = 2020;

type ReleaseErrors = {
  version?: string;
  notesUrl?: string;
  highlights?: string;
  installerUrl?: string;
  installerSha256?: string;
};

/** Field errors for a release form; empty when it may be sent. */
function validateRelease(f: {
  version?: string;
  notesUrl: string;
  highlights: string;
  installerUrl: string;
  installerSha256: string;
  installerKey?: string;
}) {
  const e: ReleaseErrors = {};
  if (f.installerUrl.trim() && !/^https:\/\/\S+$/.test(f.installerUrl.trim()))
    e.installerUrl = 'An https:// link.';
  if (f.installerSha256.trim() && !/^[0-9a-fA-F]{64}$/.test(f.installerSha256.trim()))
    e.installerSha256 = '64 hexadecimal characters.';
  else if (f.installerSha256.trim() && !f.installerUrl.trim() && !f.installerKey)
    e.installerSha256 = 'Add the installer link too.';
  if (f.version !== undefined && !versionRe.test(f.version.trim()))
    e.version = 'A version such as 1.0.0 or 1.0.0.1.';
  if (f.notesUrl.trim() && !/^https:\/\/\S+$/.test(f.notesUrl.trim()))
    e.notesUrl = 'An https:// link.';
  if ([...f.highlights.trim()].length > maxHighlights)
    e.highlights = `At most ${maxHighlights} characters.`;
  return e;
}

/** What customers read about the release on the portal's Releases page. */
function HighlightsInput({
  value,
  error,
  onChange,
}: {
  value: string;
  error?: string;
  onChange: (v: string) => void;
}) {
  return (
    <Textarea
      label="Highlights"
      description="Shown to customers on the portal's Releases page. Plain text."
      autosize
      minRows={3}
      value={value}
      error={error}
      onChange={(e) => onChange(e.currentTarget.value)}
    />
  );
}

/**
 * Where the release's installer downloads from, and its SHA-256. A release
 * CI published with a Spaces key shows the key read-only in place of the URL.
 */
function InstallerInputs({
  url,
  sha,
  installerKey,
  errors,
  onUrl,
  onSha,
}: {
  url: string;
  sha: string;
  installerKey?: string;
  errors: ReleaseErrors;
  onUrl: (v: string) => void;
  onSha: (v: string) => void;
}) {
  return (
    <>
      {installerKey ? (
        <TextInput
          label="Installer"
          description="Signed download from Spaces, set by release CI. Each download link lasts 15 minutes."
          value={installerKey}
          readOnly
          styles={{ input: mono }}
        />
      ) : (
        <TextInput
          label="Installer URL"
          description="Resellers and staff download the installer from here."
          placeholder="https://"
          value={url}
          error={errors.installerUrl}
          onChange={(e) => onUrl(e.currentTarget.value)}
        />
      )}
      <TextInput
        label="Installer SHA-256"
        description="Optional: shown so installers can verify the download."
        value={sha}
        error={errors.installerSha256}
        styles={{ input: mono }}
        onChange={(e) => onSha(e.currentTarget.value)}
      />
    </>
  );
}

/** Releases: the GoATMS versions announcements and audiences refer to. */
export function Releases() {
  const api = useApi();
  const canWrite = useCanWrite();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Release>();
  const list = useQuery({ queryKey: keys.releases, queryFn: () => api.releases.list() });

  return (
    <>
      <PageHeader
        title="Releases"
        subtitle="Adding a release starts a draft announcement for it"
        actions={
          canWrite && (
            <Button color="dark" size="sm" h={40} onClick={() => setCreating(true)}>
              New release
            </Button>
          )
        }
      />
      <Card padded={false}>
        <QueryState isPending={list.isPending} error={list.error}>
          {() =>
            list.data!.items.length === 0 ? (
              <Empty>No releases yet.</Empty>
            ) : (
              <DataTable
                label="Releases"
                minWidth={560}
                head={['Version', 'Published', 'Notes', 'Installer', ...(canWrite ? [''] : [])]}
              >
                {list.data!.items.map((r) => (
                  <Table.Tr key={r.id}>
                    <Table.Td style={{ ...mono, fontWeight: 500 }}>{r.version}</Table.Td>
                    <Table.Td>{formatDate(r.publishedAt)}</Table.Td>
                    <Table.Td>
                      {r.notesUrl ? (
                        <a href={r.notesUrl} target="_blank" rel="noreferrer">
                          Release notes
                        </a>
                      ) : (
                        '–'
                      )}
                    </Table.Td>
                    <Table.Td>
                      {r.installerUrl ? (
                        <a href={r.installerUrl} rel="noreferrer">
                          Download
                        </a>
                      ) : (
                        '–'
                      )}
                    </Table.Td>
                    {canWrite && (
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          aria-label={`Edit ${r.version}`}
                          onClick={() => setEditing(r)}
                        >
                          Edit
                        </Button>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
              </DataTable>
            )
          }
        </QueryState>
      </Card>
      {canWrite && <NewRelease opened={creating} onClose={() => setCreating(false)} />}
      {canWrite && editing && <EditRelease r={editing} onClose={() => setEditing(undefined)} />}
    </>
  );
}

function NewRelease({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [version, setVersion] = useState('');
  const [publishedOn, setPublishedOn] = useState('');
  const [notesUrl, setNotesUrl] = useState('');
  const [highlights, setHighlights] = useState('');
  const [installerUrl, setInstallerUrl] = useState('');
  const [installerSha256, setInstallerSha256] = useState('');
  const [errors, setErrors] = useState<ReleaseErrors>({});
  const create = useMutation({
    mutationFn: () =>
      api.releases.create({
        version: version.trim(),
        publishedAt: publishedOn ? fromDateInput(publishedOn) : undefined,
        notesUrl: notesUrl.trim() || undefined,
        highlights: highlights.trim() || undefined,
        installerUrl: installerUrl.trim() || undefined,
        installerSha256: installerSha256.trim() || undefined,
      }),
    onSuccess: async (res) => {
      queryClient.setQueryData(keys.announcement(res.announcement.id), res.announcement);
      await queryClient.invalidateQueries({ queryKey: keys.releases });
      await queryClient.invalidateQueries({ queryKey: keys.announcements });
      await navigate({ to: '/announcements', search: { id: res.announcement.id } });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = validateRelease({ version, notesUrl, highlights, installerUrl, installerSha256 });
    setErrors(v);
    if (Object.keys(v).length === 0) create.mutate();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="New release">
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Version"
          placeholder="1.0.0"
          data-autofocus
          value={version}
          error={errors.version}
          onChange={(e) => setVersion(e.currentTarget.value)}
        />
        <TextInput
          label="Published"
          description="Leave empty for today."
          type="date"
          value={publishedOn}
          onChange={(e) => setPublishedOn(e.currentTarget.value)}
        />
        <TextInput
          label="Release notes URL"
          placeholder="https://"
          value={notesUrl}
          error={errors.notesUrl}
          onChange={(e) => setNotesUrl(e.currentTarget.value)}
        />
        <HighlightsInput value={highlights} error={errors.highlights} onChange={setHighlights} />
        <InstallerInputs
          url={installerUrl}
          sha={installerSha256}
          errors={errors}
          onUrl={setInstallerUrl}
          onSha={setInstallerSha256}
        />
        {create.error && (
          <Alert color="red">
            {isApiError(create.error, 'duplicate')
              ? 'That version already exists.'
              : errorMessage(create.error)}
          </Alert>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" color="dark" loading={create.isPending}>
            Create release
          </Button>
        </Group>
      </form>
    </Modal>
  );
}

function EditRelease({ r, onClose }: { r: Release; onClose: () => void }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [publishedOn, setPublishedOn] = useState(toDateInput(r.publishedAt));
  const [notesUrl, setNotesUrl] = useState(r.notesUrl ?? '');
  const [highlights, setHighlights] = useState(r.highlights ?? '');
  // A keyed installer's installerUrl is Central's download route, not an
  // editable link: the form neither shows nor sends it.
  const storedUrl = r.installerKey ? '' : (r.installerUrl ?? '');
  const [installerUrl, setInstallerUrl] = useState(storedUrl);
  const [installerSha256, setInstallerSha256] = useState(r.installerSha256 ?? '');
  const [errors, setErrors] = useState<ReleaseErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: keys.releases });
    onClose();
  };
  // The PATCH carries only the fields that differ from the loaded release,
  // so an untouched date keeps its stored time of day.
  const save = useMutation({
    mutationFn: async () => {
      const req: UpdateReleaseRequest = {};
      if (publishedOn && publishedOn !== toDateInput(r.publishedAt))
        req.publishedAt = fromDateInput(publishedOn);
      if (notesUrl.trim() !== (r.notesUrl ?? '')) req.notesUrl = notesUrl.trim();
      if (highlights.trim() !== (r.highlights ?? '')) req.highlights = highlights.trim();
      if (installerUrl.trim() !== storedUrl) req.installerUrl = installerUrl.trim();
      if (installerSha256.trim().toLowerCase() !== (r.installerSha256 ?? ''))
        req.installerSha256 = installerSha256.trim();
      if (Object.keys(req).length > 0) await api.releases.update(r.id, req);
    },
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => api.releases.delete(r.id), onSuccess: done });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = validateRelease({
      notesUrl,
      highlights,
      installerUrl,
      installerSha256,
      installerKey: r.installerKey,
    });
    setErrors(v);
    if (Object.keys(v).length === 0) save.mutate();
  };

  return (
    <Modal opened onClose={onClose} title={`Release ${r.version}`}>
      <form
        onSubmit={submit}
        noValidate
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <TextInput
          label="Published"
          type="date"
          value={publishedOn}
          onChange={(e) => setPublishedOn(e.currentTarget.value)}
        />
        <TextInput
          label="Release notes URL"
          placeholder="https://"
          value={notesUrl}
          error={errors.notesUrl}
          onChange={(e) => setNotesUrl(e.currentTarget.value)}
        />
        <HighlightsInput value={highlights} error={errors.highlights} onChange={setHighlights} />
        <InstallerInputs
          url={installerUrl}
          sha={installerSha256}
          installerKey={r.installerKey}
          errors={errors}
          onUrl={setInstallerUrl}
          onSha={setInstallerSha256}
        />
        {(save.error ?? remove.error) && (
          <Alert color="red">{errorMessage(save.error ?? remove.error)}</Alert>
        )}
        <Group justify="space-between">
          {confirmDelete ? (
            <Group gap={8}>
              <Button color="red" loading={remove.isPending} onClick={() => remove.mutate()}>
                Delete {r.version}
              </Button>
              <Button variant="default" onClick={() => setConfirmDelete(false)}>
                Keep
              </Button>
            </Group>
          ) : (
            <Button variant="subtle" color="red" onClick={() => setConfirmDelete(true)}>
              Delete release
            </Button>
          )}
          <Group gap={8}>
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" color="dark" loading={save.isPending}>
              Save
            </Button>
          </Group>
        </Group>
        {confirmDelete && (
          <span style={{ fontSize: 13 }}>Its announcements stay; only the release is removed.</span>
        )}
      </form>
    </Modal>
  );
}
